import React, {
  useCallback,
  useState,
  ReactNode,
  useRef,
  useMemo,
} from "react";
import { useComponentSize } from "../services/hooks";

type Props = {
  tabTitles: string[];
  tabContents: ReactNode[];
};

export default function Tabs({ tabContents, tabTitles }: Props) {
  const [selectedTab, setSelectedTab] = useState(0);

  const [tabRefs, setTabRefs] = useState<HTMLElement[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sizeOfWrapper = useComponentSize(wrapperRef);
  const Content = useCallback(() => {
    return tabContents[selectedTab];
  }, [selectedTab, tabContents]);

  const selectorStyles = useMemo(() => {
    const currentTab = tabRefs[selectedTab];
    if (currentTab && sizeOfWrapper) {
      const box = currentTab.getBoundingClientRect();

      const wrapper = wrapperRef.current;
      const wrapperLeft = sizeOfWrapper.left;
      const scrollLeft = wrapper?.scrollLeft ?? 0;

      return {
        width: box.width,
        left: box.left - wrapperLeft + scrollLeft,
        height: box.height,
      };
    }
    return {};
  }, [selectedTab, tabRefs, sizeOfWrapper?.left]);

  return (
    <div className="m-5">
      <div className="d-flex tab-titles overflow-auto" ref={wrapperRef}>
        <div className="tab-selector" style={selectorStyles} />
        {tabTitles.map((title, index) => (
          <div
            ref={(el) => {
              if (el && tabRefs[index] !== el) {
                setTabRefs((prev) => {
                  const newRefs = [...prev];
                  newRefs[index] = el;
                  return newRefs;
                });
              }
            }}
            key={index}
            className={`tab-title ${selectedTab === index ? "active" : ""}`}
            onClick={() => setSelectedTab(index)}
          >
            {title}
          </div>
        ))}
      </div>
      <div className="tab-content">{Content()}</div>
    </div>
  );
}
